import { useEffect, useState } from "react"
import { InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { confirm } from "lib/@dialogs/confirm"

export function CostMultiplierInput({ multiplier, onChange }) {
    const percentage = multiplierToPercentage(multiplier ?? 1.0)
    const sign = percentage >= 0 ? "+" : "-"
    const [localValue, setLocalValue] = useState(Math.abs(percentage))

    useEffect(syncFromMultiplier, [multiplier])

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <ToggleButtonGroup exclusive size="small" value={sign} onChange={handleSignChange}>
                <ToggleButton value="+">+</ToggleButton>
                <ToggleButton value="-">&minus;</ToggleButton>
            </ToggleButtonGroup>
            <TextField
                size="small"
                type="number"
                value={localValue}
                onChange={handleValueChange}
                onBlur={handleBlur}
                onWheel={(e) => e.target.blur()}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 100 }}
                InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
            />
            <Typography variant="caption" color="text.secondary">
                = {formatMultiplier(multiplier ?? 1.0)}x
            </Typography>
        </Stack>
    )

    function syncFromMultiplier() {
        const pct = multiplierToPercentage(multiplier ?? 1.0)
        setLocalValue(Math.abs(pct))
    }

    function handleSignChange(_, newSign) {
        if (!newSign) return
        const newMultiplier = percentageToMultiplier(newSign === "-" ? -localValue : localValue)
        applyMultiplier(newMultiplier)
    }

    async function handleValueChange(e) {
        const val = parseFloat(e.target.value)
        // The toggle owns the sign, so a typed minus must not quietly flip the direction.
        setLocalValue(Number.isFinite(val) ? Math.abs(val) : 0)
    }

    async function handleBlur() {
        const newMultiplier = percentageToMultiplier(sign === "-" ? -localValue : localValue)
        await applyMultiplier(newMultiplier)
    }

    async function applyMultiplier(newMultiplier) {
        if (newMultiplier <= 0) {
            const useExclude = await confirm(
                "A multiplier of 0 or below would remove all cost. Consider using the Exclude action instead.",
                "Invalid Cost Modifier"
            )
            if (useExclude) {
                setLocalValue(0)
                onChange(1.0)
                return
            }
            // Declined, so the multiplier is unchanged and the field has to say so.
            syncFromMultiplier()
            return
        }
        if (newMultiplier > 5.0) {
            const ok = await confirm(
                `A multiplier of ${formatMultiplier(newMultiplier)}x is very high. Are you sure?`,
                "High Cost Modifier"
            )
            if (!ok) {
                syncFromMultiplier()
                return
            }
        }
        onChange(newMultiplier)
    }
}

// Rounding kills the binary artifacts of x100 / division (1.2 reads back as 19.999999999999996)
// without flattening real fractions: a 1.125 multiplier stays 12.5%, where rounding to whole
// percent used to show 13% and write 1.13 back on blur.
function multiplierToPercentage(m) {
    return roundTo((m - 1) * 100, 6)
}

function percentageToMultiplier(p) {
    return roundTo(1 + p / 100, 8)
}

function roundTo(value, places) {
    const factor = 10 ** places
    return Math.round(value * factor) / factor
}

function formatMultiplier(m) {
    return (Math.round(m * 100) / 100).toFixed(2)
}
